import React from 'react'

import { Box, Typography } from '@mui/material'
import { surfaceRadius } from 'settings/brandRadius'
import { Avatar, Skeleton } from '@repo/ui'
import { useQRCode } from 'next-qrcode'

export interface QRCodePanelProps {
  /**
   * What the QR encodes. Undefined/empty renders the reserved placeholder at
   * the same footprint, so the panel does not resize when the value arrives.
   */
  value?: string
  /** Rendered edge length of the QR itself, in CSS px. */
  size?: number
  /** Optional line beneath the code — typically the handle the code resolves to. */
  caption?: React.ReactNode
  /**
   * A photo to sit in a circle at the dead centre of the code (the Venmo /
   * WeChat treatment). Omit for a plain code.
   *
   * Setting this ALSO raises the error-correction level to `H` — see
   * `INSET_DIAMETER_RATIO` below for why the two are one decision and must not
   * be settable apart.
   */
  insetImageUrl?: string
  /** Accessible name for the inset photo. */
  insetImageAlt?: string
  /**
   * Initials to draw in the inset when there is no photo.
   *
   * Not a nicety. Without it a code carries an inset only for the people who
   * happen to have uploaded a photo, so the SAME feature ships at level `H` for
   * some users and `M` for others — two different physical robustnesses, chosen
   * by something the user cannot see and did not decide. The identity argument
   * survives the missing photo too: initials in the middle still tell two people
   * holding up two codes which one is whose, which is the whole reason the inset
   * exists.
   *
   * So: either of `insetImageUrl` or `insetInitials` turns the inset on, and the
   * error-correction level with it.
   */
  insetInitials?: string
}

/**
 * The inset's diameter as a fraction of the rendered QR edge.
 *
 * An inset does not "sit on top of" a QR, it DESTROYS the modules underneath it —
 * the decoder recovers them from Reed-Solomon parity, and only up to the level's
 * budget. So the diameter and the error-correction level are a single decision:
 *
 *   level `M` recovers ~15% of codewords, `H` ~30%.
 *   a centred circle at 22% of the edge covers π·0.22²/4 ≈ 3.8% of the frame,
 *   ~4.9% of the code region once the 3-module quiet zone is discounted.
 *
 * That is a wide margin under H, and deliberately so: the recovery budget is
 * shared with real-world damage (glare, a curved phone screen, a creased
 * printout, a low-resolution photo of the code), so spending most of it on a
 * decoration would trade a decoration for scans that fail in exactly the
 * conditions this app is used in. 22% is large enough to read as a face on a
 * 240px card and small enough that the code survives a bad photograph.
 *
 * Raise this and re-run the jsQR decode check before believing it still scans.
 */
const INSET_DIAMETER_RATIO = 0.22

/**
 * The white ring around the photo, as a fraction of the edge.
 *
 * Not decoration: without it the photo's own dark pixels abut live modules, and
 * a decoder scanning that boundary can read the photo's edge as module data. The
 * ring guarantees a quiet boundary, exactly as the code's outer `margin` does.
 */
const INSET_RING_RATIO = 0.03

/**
 * The house avatar surface, as literal values.
 *
 * These are `@repo/shadcn-ui`'s `Avatar` measured in the browser — the treatment
 * the Profile page's own avatar renders (gray-200 fill, gray-300 hairline,
 * gray-500 initials at weight 400, glyph at 0.40 of the diameter). They are
 * copied as CONSTANTS rather than read from the theme for the same reason the
 * card is forced `#FFFFFF` and the modules forced black: this disc sits INSIDE
 * a QR code, and a themed fill would put a dark region in the middle of the
 * modules under a dark theme or a dark tenant palette. `@repo/ui`'s `Avatar`
 * defaults to `primary[100]` / `text.secondary`, both of which do exactly that.
 *
 * So the shared component supplies the BEHAVIOUR (photo → initials → person
 * icon, the two-letter `getInitials`, the broken-image fallback) and this
 * supplies the one thing a themed component cannot safely supply here.
 */
const INSET_FILL = '#E5E7EB'
const INSET_BORDER = '#D1D5DB'
const INSET_INITIALS_COLOR = '#6B7280'
const INSET_INITIALS_RATIO = 0.4

/**
 * A QR code on a white card.
 *
 * VERA-304: extracted on the SECOND hand-roll of `useQRCode` in this app
 * (working-conventions §36) — `CertificatesNfcScanModal` was the first, and the
 * profile code would have been a third copy of the same options object. The
 * options are the thing worth centralising: error-correction level, quiet-zone
 * margin and the black-on-white pair are scannability parameters, not styling
 * choices, and they must not drift per call site.
 *
 * The surface is FORCED white (`#FFFFFF`, not `background.paper`) and the
 * modules forced black. A QR is read by a camera looking for high contrast
 * between light and dark modules; inheriting a themed background renders the
 * code dark-on-dark under a dark theme or a dark tenant palette, at which point
 * it simply does not scan. The card's own padding sits outside the code's
 * `margin: 3` quiet zone, so the required clear border survives however the
 * caller places the panel.
 */
export const QRCodePanel = ({
  value,
  size = 240,
  caption,
  insetImageUrl,
  insetImageAlt = '',
  insetInitials,
}: QRCodePanelProps) => {
  const { Canvas } = useQRCode()

  const hasInset = Boolean(insetImageUrl || insetInitials)
  const insetDiameter = Math.round(size * INSET_DIAMETER_RATIO)
  const ringDiameter = Math.round(size * (INSET_DIAMETER_RATIO + INSET_RING_RATIO * 2))

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        // VRG-562: follows the tenant's Corner radius.
        borderRadius: surfaceRadius,
        border: '1px solid rgba(0, 0, 0, 0.08)',
        // 16px. `apps/app` runs MUI's default 8px base, not @repo/ui's 4px, so
        // spacing factors mean twice what they do in the platform (§29) — px here.
        p: '16px',
        width: 'fit-content',
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: caption ? '12px' : 0,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          /*
           * qrcode's `width` option is a FLOOR, not an exact size: it picks
           * `scale = ceil(width / modules)` and emits `modules * scale`, which
           * for a URL of this length overshoots 240 by a good 30px. Left
           * unconstrained the canvas would be wider than the box reserved for
           * it, so the panel would grow the moment the value arrived — the
           * layout jump the placeholder exists to prevent (§19). Pinning the
           * canvas to `size` in CSS keeps the bitmap oversampled (never
           * upscaled) while the footprint stays fixed.
           */
          '& canvas': { display: 'block', width: `${size}px`, height: `${size}px` },
        }}
      >
        {value ? (
          <>
            <Canvas
              text={value}
              options={{
                // See INSET_DIAMETER_RATIO: the inset destroys modules, so the
                // level is not a free choice once a photo is in the middle.
                errorCorrectionLevel: hasInset ? 'H' : 'M',
                margin: 3,
                scale: 4,
                width: size,
                color: { dark: '#000000', light: '#FFFFFF' },
              }}
            />

            {hasInset && (
              // Centred on the CANVAS, which is centred on the code — the
              // `margin: 3` quiet zone is symmetric, so the canvas centre and
              // the code's centre are the same point.
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: ringDiameter,
                  height: ringDiameter,
                  borderRadius: '50%',
                  // Forced white for the same reason the card is: a themed
                  // surface here would put a dark disc in the middle of the
                  // code and take the boundary with it.
                  backgroundColor: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  // Decoration over a code that is already complete without it.
                  pointerEvents: 'none',
                }}
              >
                {/*
                  The SHARED avatar, not a disc of this component's own.

                  It was a hand-rolled pair — an `<img>` branch and a black
                  circle with white letters — which made this the app's THIRD
                  initials renderer and, worse, put a DIFFERENT rendering of one
                  person on two adjacent surfaces: the Profile page shows a grey
                  circle with dark initials and the code directly under it showed
                  a black disc with white ones. Same user, same identity, two
                  looks (working-conventions §36/§47).

                  `@repo/ui`'s `Avatar` rather than `@repo/shadcn-ui`'s — which
                  is the one the Profile page itself uses — for one hard reason:
                  the shadcn avatar sizes off a fixed Tailwind scale (`lg` 48,
                  `xl` 64) and this diameter is derived from the QR's edge by
                  `INSET_DIAMETER_RATIO`, because the diameter and the
                  error-correction level are one decision. A fixed-scale avatar
                  would either break that coupling or silently change the covered
                  area when a caller passes a different `size`. `@repo/ui`'s
                  takes px. Its house LOOK is restored by the pinned constants
                  above, so the two surfaces match.
                */}
                <Avatar
                  src={insetImageUrl}
                  initials={insetInitials}
                  alt={insetImageAlt}
                  size={insetDiameter}
                  sx={{
                    backgroundColor: INSET_FILL,
                    color: INSET_INITIALS_COLOR,
                    /*
                       `!important` is required, not stylistic: `index.css`'s
                       `#vera-shadow-root * { border-color: … }` wins on ID
                       specificity over any `sx` value, and it does — measured
                       rgb(228,228,231) here instead of the house gray-300 until
                       this was added. Same fix, same reason, as
                       `fieldMetrics.toolbarControlSx`.
                    */
                    border: `1px solid ${INSET_BORDER} !important`,
                    fontSize: Math.round(insetDiameter * INSET_INITIALS_RATIO),
                    fontWeight: 400,
                    lineHeight: 1,
                    userSelect: 'none',
                  }}
                />
              </Box>
            )}
          </>
        ) : (
          // Explicit tint: MUI's skeleton derives its base from the palette, so
          // under a dark theme it would be a near-white block on this forced-white
          // card — invisible.
          <Skeleton variant='rounded' width={size} height={size} sx={{ bgcolor: 'rgba(0, 0, 0, 0.08)' }} />
        )}
      </Box>

      {caption ? (
        // Explicit dark text for the same reason the surface is explicit white:
        // `text.primary` is white under a dark theme and would vanish here.
        <Typography
          sx={{
            color: '#111111',
            fontSize: '15px',
            fontWeight: 500,
            lineHeight: 1.5,
            textAlign: 'center',
            wordBreak: 'break-word',
          }}
        >
          {caption}
        </Typography>
      ) : null}
    </Box>
  )
}

export default QRCodePanel
